import axios from 'axios';
import { apiUrl as getAPIUrl, authHeader as getAuthToken } from '../runtime';
import { getAsRole, isReadOnly } from './session';
import { emptyDoc } from './emptyDoc';
import { REJECT, REQUEST_TIMEOUT_MS, TRANSFER_TIMEOUT_MS } from '../../constants';

const base = (bookingId, briefUuid) =>
  getAPIUrl(`/api/booking/wedding-day/${bookingId}/${briefUuid}/`);

const headers = () => ({ Authorization: getAuthToken() });

// A staff read carries the side it is made as, because the saved answers hang
// off a participant rather than off the brief.
const withRole = (url) => {
  const role = getAsRole();
  return role ? `${url}?as_role=${encodeURIComponent(role)}` : url;
};

// Every write answers with `success`. A false one is the server refusing this
// body, so it is raised as a rejection: retrying would only repeat it.
const unwrap = (data) => {
  if (data && data.success === false) {
    const err = new Error(data.message || 'That could not be saved.');
    err.rejected = true;
    err.code = data.code;
    throw err;
  }
  return data;
};

// Nothing may be written without a deep link, or once the payload has turned
// the form read-only. Last guard before a request leaves the app.
const blocked = (briefUuid) => isReadOnly() || !briefUuid;

// Normalises the parts of the payload every consumer reads, so a partial
// response from a poll cannot blank a list the form is rendering from.
const shape = (data) => ({
  ...data,
  sections: data.sections || [],
  events: data.events || [],
  venues: data.venues || [],
  package_windows: data.package_windows || {},
  // The opening hour alone, for a day sold without a closing one.
  package_opens: data.package_opens || {},
  package_span: data.package_span || null,
  interview_question: data.interview_question || null,
  locks_at: data.locks_at || null,
  contacts: data.contacts || [],
  ritual_options: data.ritual_options || [],
  needs_interview: data.needs_interview === true,
  interviews: data.interviews || [],
  limits: data.limits || {},
  family: data.family || {},
  change_log: data.change_log || [],
  // Which side this user is, as the server reads it off the booking. Never
  // read off the link.
  role: data.role || null,
  // The first submit by either side closes the shared sections for both.
  shared_locked: data.shared_locked === true,
  answers: {
    // emptyDoc() first, so the envelope keys exist on an untouched form.
    ...emptyDoc(),
    ...(data.answers || {}),
    // The server's status owns the lock. A form ops has reopened comes back
    // as 'in progress' and must unlock, even though the answer document it
    // was submitted from still says otherwise.
    submitted: data.status === 'submitted',
    locked: data.locked === true,
  },
});

// GET wedding-day/<booking_id>/<brief_uuid>/
// Carries the whole form: the event entries in order, the sections and their
// fields, the saved answers, the venue catalogue for the booking's city, the
// package windows and the limits. Nothing about the shape is declared here.
export const bootstrap = async ({ bookingId, briefUuid }) => {
  const { data } = await axios.get(withRole(base(bookingId, briefUuid)), {
    headers: headers(),
    timeout: REQUEST_TIMEOUT_MS,
  });
  // Not a failure to retry: the server refused this brief for this user, and
  // the page says so. A thrown error would be indistinguishable from a
  // dropped connection.
  if (data.success === false) {
    return { denied: true, code: data.code, message: data.message };
  }
  return shape(data);
};

// GET wedding-day/<booking_id>/<brief_uuid>/status/
// Progress, both sides and the change log.
export const fetchStatus = async ({ bookingId, briefUuid }) => {
  const { data } = await axios.get(withRole(`${base(bookingId, briefUuid)}status/`), {
    headers: headers(),
    timeout: REQUEST_TIMEOUT_MS,
  });
  return unwrap(data);
};

// GET wedding-day/<booking_id>/<brief_uuid>/submission-preview/
// Exactly what submitting would put in front of ops, written nowhere. On a
// reopened form that is only what the couple has changed since.
export const fetchSubmissionPreview = async ({ bookingId, briefUuid }) => {
  const { data } = await axios.get(
    `${base(bookingId, briefUuid)}submission-preview/`,
    { headers: headers(), timeout: REQUEST_TIMEOUT_MS }
  );
  return unwrap(data);
};

// PATCH contacts/
// One side's coordinator or backup, addressed by side and kind. Debounced by
// the caller, like the rest of the free text on the form.
export const patchContact = async ({ bookingId, briefUuid, side, kind, payload }) => {
  if (blocked(briefUuid)) return { contacts: null };
  const { data } = await axios.patch(
    `${base(bookingId, briefUuid)}contacts/`,
    { side, kind, ...payload },
    { headers: headers(), timeout: REQUEST_TIMEOUT_MS }
  );
  return unwrap(data);
};

// PATCH interviews/
// One interview sitting, addressed by side and group rather than by id: the
// grid is fixed, so the client answers a question the server already knows.
export const patchInterview = async ({ bookingId, briefUuid, side, group, payload }) => {
  if (blocked(briefUuid)) return { interviews: null };
  const { data } = await axios.patch(
    `${base(bookingId, briefUuid)}interviews/`,
    { side, group, ...payload },
    { headers: headers(), timeout: REQUEST_TIMEOUT_MS }
  );
  return unwrap(data);
};

// PATCH events/order/
// The full order for one date and one side, so a repeat is harmless. A custom
// ritual is inserted by the same call. The response's `cleared` lists the
// entries that lost their timing because of the move.
export const patchEventOrder = async ({ bookingId, briefUuid, order, addRitual, removeEntryId }) => {
  if (blocked(briefUuid)) return { events: null, cleared: [] };
  const body = { order };
  if (addRitual) body.add_ritual = addRitual;
  // Only a custom ritual is removable, which the caller has already checked.
  if (removeEntryId) body.remove_entry_id = removeEntryId;
  const { data } = await axios.patch(`${base(bookingId, briefUuid)}events/order/`, body, {
    headers: headers(),
    timeout: REQUEST_TIMEOUT_MS,
  });
  return unwrap(data);
};

// PATCH events/<entry_id>/
// The event's own name. The couple renames what a ritual or a package event is
// called on their day, and the name is all this route carries.
export const patchEventName = async ({ bookingId, briefUuid, entryId, name }) => {
  if (blocked(briefUuid)) return { entry: null };
  const { data } = await axios.patch(
    `${base(bookingId, briefUuid)}events/${entryId}/`,
    { name },
    { headers: headers(), timeout: REQUEST_TIMEOUT_MS }
  );
  return unwrap(data);
};

// PATCH events/<entry_id>/couple-shot/
// Where the couple shot sits against its event: the start of it, the end of
// it, or a half hour inside it. It travels with the event, so its place is
// all there is to say about it, plus the time when it runs inside.
export const patchCoupleShot = async ({ bookingId, briefUuid, entryId, position, start }) => {
  if (blocked(briefUuid)) return { couple_shot: null };
  // Either the place it sits or the half hour it runs in, never both: moving
  // it to an end drops the time that only made sense inside the event.
  const body = position === undefined ? { start } : { position };
  const { data } = await axios.patch(
    `${base(bookingId, briefUuid)}events/${entryId}/couple-shot/`,
    body,
    { headers: headers(), timeout: REQUEST_TIMEOUT_MS }
  );
  return unwrap(data);
};

// PATCH interviews/choice/
// Whether this couple wants the interviews at all. The same question the
// creative preferences form asks, so answering it here answers it there.
export const patchInterviewChoice = async ({ bookingId, briefUuid, wantsInterview }) => {
  if (blocked(briefUuid)) return { wants_interview: wantsInterview };
  const { data } = await axios.patch(
    `${base(bookingId, briefUuid)}interviews/choice/`,
    { wants_interview: wantsInterview },
    { headers: headers(), timeout: REQUEST_TIMEOUT_MS }
  );
  return unwrap(data);
};

// PATCH events/<entry_id>/confirmed/
// That the couple has closed this card. Nothing on the form is compulsory, so
// closing a card is the answer, and it is stored rather than kept in the page
// so a reload does not reopen what they already walked past.
export const patchEventConfirmed = async ({ bookingId, briefUuid, entryId, confirmed }) => {
  if (blocked(briefUuid)) return { confirmed };
  const { data } = await axios.patch(
    `${base(bookingId, briefUuid)}events/${entryId}/confirmed/`,
    { confirmed },
    { headers: headers(), timeout: REQUEST_TIMEOUT_MS }
  );
  return unwrap(data);
};

// PATCH events/<entry_id>/side/
// Whose event this is, where the estimation left that open. An event sold to
// one side comes back as side_locked.
export const patchEventSide = async ({ bookingId, briefUuid, entryId, side }) => {
  if (blocked(briefUuid)) return { side: null };
  const { data } = await axios.patch(
    `${base(bookingId, briefUuid)}events/${entryId}/side/`,
    { side },
    { headers: headers(), timeout: REQUEST_TIMEOUT_MS }
  );
  return unwrap(data);
};

// PATCH events/<entry_id>/timing/
// One event's date, start, end and venue. Nothing here saves any more: every
// timing edit becomes a request for ops to review, answered with the values
// awaiting them. The map link is derived from the venue server side, so it is
// not sent. A clash or an unknown venue is still a refusal.
export const patchEventTiming = async ({ bookingId, briefUuid, entryId, payload }) => {
  if (blocked(briefUuid)) return { pending: null };
  const { data } = await axios.patch(
    `${base(bookingId, briefUuid)}events/${entryId}/timing/`,
    payload,
    { headers: headers(), timeout: REQUEST_TIMEOUT_MS }
  );
  // needs_approval is the ordinary outcome, not a rejection: the request was
  // recorded and the approved schedule is unchanged until ops says otherwise.
  if (data && data.success === false && data.code === REJECT.APPROVAL) {
    return {
      pending: data.pending || {},
      editRequestId: data.edit_request_id,
      revision: data.revision,
    };
  }
  return unwrap(data);
};

// PATCH section/<section_key>/
// Always carries the section's full current value, so a repeat is harmless.
// The server routes it by the section's scope, which is what keeps Day Notes
// private and the rest shared.
export const patchSection = async ({ bookingId, briefUuid, sectionKey, payload, doc }) => {
  if (blocked(briefUuid)) return { revision: doc.revision };
  const { data } = await axios.patch(
    `${base(bookingId, briefUuid)}section/${sectionKey}/`,
    payload,
    { headers: headers(), timeout: REQUEST_TIMEOUT_MS }
  );
  return unwrap(data);
};

// POST family/photos/
// The faces the browser found on one photograph. The photograph itself stays
// on the device: detection and cropping both happen there, so what travels is
// a list of boxes.
export const postPhotoFaces = async ({ bookingId, briefUuid, side, faces }) => {
  if (blocked(briefUuid)) return null;
  const { data } = await axios.post(
    `${base(bookingId, briefUuid)}family/photos/`,
    { side, faces },
    { headers: headers(), timeout: REQUEST_TIMEOUT_MS }
  );
  return unwrap(data);
};

// GET face-model/
// The detector, served out of the API's static folder. Fetched here rather
// than by the runtime itself so it travels with our own auth header, and
// cached for a year, so this is one request per browser.
export const fetchFaceModel = async () => {
  const { data } = await axios.get(
    getAPIUrl('/api/booking/wedding-day/face-model/'),
    { headers: headers(), responseType: 'arraybuffer', timeout: TRANSFER_TIMEOUT_MS }
  );
  return new Uint8Array(data);
};

// DELETE family/photos/<photo_id>/
// Takes one photograph off its side. The server answers with the side's
// panels as they now stand, so the faces that went with it leave too.
export const deletePhoto = async ({ bookingId, briefUuid, photoId }) => {
  if (blocked(briefUuid)) return null;
  const { data } = await axios.delete(
    `${base(bookingId, briefUuid)}family/photos/${photoId}/`,
    { headers: headers(), timeout: REQUEST_TIMEOUT_MS }
  );
  return unwrap(data);
};

// The presigned POST itself. Every field S3 signed goes in first and the file
// last, because S3 ignores anything after the file part. No Authorization
// header: the signature is the credential, and ours would be rejected.
export const uploadToS3 = async ({ upload, file }) => {
  const body = new FormData();
  Object.keys(upload.fields || {}).forEach((key) => body.append(key, upload.fields[key]));
  body.append('file', file);
  await axios.post(upload.url, body, { timeout: TRANSFER_TIMEOUT_MS });
};

// POST family/faces/<tag_id>/crop/ (first call)
// Asks for somewhere to put one face. Answers with the presigned S3 POST, so
// the crop itself never travels through our API.
export const createCropSlot = async ({ bookingId, briefUuid, tagId }) => {
  if (blocked(briefUuid)) return null;
  const { data } = await axios.post(
    `${base(bookingId, briefUuid)}family/faces/${tagId}/crop/`,
    {},
    { headers: headers(), timeout: REQUEST_TIMEOUT_MS }
  );
  return unwrap(data);
};

// POST family/faces/<tag_id>/crop/ (second call)
// The crop is in the bucket, so the face can point at it.
export const finishCrop = async ({ bookingId, briefUuid, tagId }) => {
  if (blocked(briefUuid)) return null;
  const { data } = await axios.post(
    `${base(bookingId, briefUuid)}family/faces/${tagId}/crop/`,
    { uploaded: true },
    { headers: headers(), timeout: REQUEST_TIMEOUT_MS }
  );
  return unwrap(data);
};

// PATCH family/faces/<tag_id>/
// Only the selection and the relationship are ever written: the boxes are the
// detector's, not the client's.
export const patchFace = async ({ bookingId, briefUuid, tagId, payload }) => {
  if (blocked(briefUuid)) return { tag: null };
  const { data } = await axios.patch(
    `${base(bookingId, briefUuid)}family/faces/${tagId}/`,
    payload,
    { headers: headers(), timeout: REQUEST_TIMEOUT_MS }
  );
  return unwrap(data);
};

// POST events/<entry_id>/activities/
// An activity or a surprise. A surprise is the same row with creator only
// visibility, so the partner's read is filtered server side.
export const createActivity = async ({ bookingId, briefUuid, entryId, payload }) => {
  if (blocked(briefUuid)) return { activity: null };
  const { data } = await axios.post(
    `${base(bookingId, briefUuid)}events/${entryId}/activities/`,
    payload,
    { headers: headers(), timeout: REQUEST_TIMEOUT_MS }
  );
  return unwrap(data);
};

// PATCH activities/<activity_id>/
// Edits one, or retires it with `is_active: false`, which keeps the change log
// intact server side. Only the author may: everyone else gets
// not_your_activity, so the caller hides the controls rather than relying on
// the refusal.
export const patchActivity = async ({ bookingId, briefUuid, activityId, payload }) => {
  if (blocked(briefUuid)) return { activity: null };
  const { data } = await axios.patch(
    `${base(bookingId, briefUuid)}activities/${activityId}/`,
    payload,
    { headers: headers(), timeout: REQUEST_TIMEOUT_MS }
  );
  return unwrap(data);
};

// POST submit/
// The acknowledgement gate, per side. The first one locks the shared sections
// for both, and dispatches the pending edit requests as one batch.
export const submitWeddingDay = async ({ bookingId, briefUuid, acknowledged }) => {
  // Null rather than a made up "submitted": nothing was sent, so nothing
  // may be locked on the strength of it.
  if (blocked(briefUuid)) return null;
  // The server refuses anything but literal true, so the tick gates the call
  // rather than only the styling.
  if (acknowledged !== true) {
    const err = new Error('Please acknowledge the note before submitting.');
    err.rejected = true;
    err.code = 'not_acknowledged';
    throw err;
  }
  const { data } = await axios.post(
    `${base(bookingId, briefUuid)}submit/`,
    { acknowledged: true },
    { headers: headers(), timeout: REQUEST_TIMEOUT_MS }
  );
  if (data.success === false) {
    const err = new Error(data.message);
    // Every submit failure is permanent; none is worth retrying.
    err.rejected = true;
    err.code = data.code;
    err.pendingSections = data.pending_sections || [];
    throw err;
  }
  return data;
};
